import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const windsor: Competitor = {
  "id": "windsor",
  "name": "Windsor.ai",
  "group": "ecomm",
  "website": "https://windsor.ai",
  "owner": "unknown (independent company; ownership not verified)",
  "summary": "Windsor.ai is a no-code data integration tool that pulls marketing, ecommerce and other SaaS data from 350+ sources into BI tools, spreadsheets, warehouses and AI assistants. It is aimed at marketers and small data teams who want connectors without running their own pipelines.",
  "bestFor": [
    "Brands or agencies that mainly need ad, Shopify and Amazon data piped into Looker Studio, Sheets, Power BI or a warehouse on a low monthly budget",
    "Teams that want to query marketing data from ChatGPT, Claude or other AI tools through an MCP server",
    "Small teams with no data engineer who are fine building reports on top of raw connector tables"
  ],
  "notFor": [
    "Brands that need modeled ecommerce finance (net revenue after refunds, fees, COGS, contribution margin) out of the box",
    "Teams looking for a built-in attribution or marketing mix modeling product with its own tracking"
  ],
  "strengths": [
    "Much cheaper to start than a custom build: paid plans begin at $19/month billed annually, and every plan includes all sources and destinations, including Snowflake and BigQuery",
    "Connector maintenance is outsourced: Shopify, Amazon Seller Central (including referral, FBA and storage fee fields), TikTok Shop, NetSuite, Recharge, Klaviyo and the main ad platforms are prebuilt, so nobody has to maintain API pipelines",
    "Vendor says a connection takes minutes; a custom Snowflake + dbt + Sigma project usually takes weeks before the first dashboard",
    "Ships ready-made report templates for Looker Studio, Power BI, Sheets and Tableau",
    "Strong AI access path: MCP server, Claude/ChatGPT/Copilot/Cursor connectors and, since May 2026, write actions on Facebook Ads and Google Ads campaigns",
    "Refresh down to every 15 minutes on Professional and Enterprise plans"
  ],
  "limitations": [
    "Transformations are formula-based custom fields (spreadsheet-style functions); the docs do not describe SQL modeling or how cross-source blends are joined, so refund, fee and COGS logic across channels still has to be built downstream",
    "Custom-field functions can silently return the original input when a conversion or regex fails, per Windsor's own docs",
    "Shopify-to-Snowflake page lists field counts but no documented target schema or data model, so you still model the raw tables yourself",
    "Plan limits on data sources (3 to 14 on self-serve tiers) and accounts mean multi-brand or multi-region setups move up tiers quickly"
  ],
  "pricing": {
    "model": "tiered subscription by number of data sources and accounts",
    "publicStartingPrice": "$19/month billed annually (Basic, $23 month-to-month)",
    "freeTier": "yes: 30-day trial (10 sources, 15 accounts), then a Forever Free plan with 1 user, 1 data source, 1 account",
    "notes": "Tiers (annual / monthly price per month): Basic $19/$23 (3 sources, 75 accounts, daily sync, 5 destination tasks); Standard $99/$118 (7 sources, 75 accounts, hourly, unlimited destination tasks); Plus $249/$299 (10 sources, 200 accounts); Professional $499/$598 (14 sources, 500 accounts, 15-minute refresh); Enterprise by quote (up to 200 sources and 50,000 accounts, SSO, white-label, custom connectors). Cost drivers: number of data sources, total accounts across sources, refresh frequency, destination tasks on the lowest tiers. Users unlimited on paid plans; all destinations on all plans. Changelog notes volume-based pricing for database destinations launched July 2025, but the pricing docs do not describe it. Setup time: vendor claims a connection in under 2 minutes; Shopify and Snowflake-side prep is extra and not timed.",
    "sourceUrl": "https://windsor.ai/price-and-plans/",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Native connector; vendor cites 122 metrics and 313 dimensions",
      "sourceUrl": "https://windsor.ai/connect/shopify-snowflake-integration/"
    },
    "amazon": {
      "value": "yes",
      "note": "Seller Central connector with orders, fees (referral, FBA, storage), inventory; Vendor Central also listed",
      "sourceUrl": "https://windsor.ai/connectors/amazon-seller-central/"
    },
    "tiktok_shop": {
      "value": "yes",
      "note": "Listed as a connector; field list not checked",
      "sourceUrl": "https://windsor.ai/connectors/"
    },
    "wholesale_retail_or_erp": {
      "value": "yes",
      "note": "NetSuite listed; Faire and ShipStation not listed",
      "sourceUrl": "https://windsor.ai/connectors/"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Facebook/Meta Ads, Google Ads, TikTok Ads and many others",
      "sourceUrl": "https://windsor.ai/connectors/"
    },
    "first_party_pixel_attribution": {
      "value": "unknown",
      "note": "Offers an attribution Looker Studio connector and dashboard template built on platform data; no own tracking pixel found",
      "sourceUrl": "https://www.windsor.ai/data-driven-attribution-for-affiliate-roi-connexity-awin-shopbots-et-al/"
    },
    "mmm_incrementality": {
      "value": "unknown",
      "note": "No MMM or incrementality product found on pricing, connector or attribution pages",
      "sourceUrl": "https://windsor.ai/price-and-plans/"
    },
    "profit_cogs": {
      "value": "unknown",
      "note": "Amazon fee fields exist, but no built-in profit or contribution margin model found",
      "sourceUrl": "https://windsor.ai/connectors/amazon-seller-central/"
    },
    "ltv_cohorts": {
      "value": "unknown",
      "note": "Vendor mentions customer lifetime value data from Shopify; no cohort/retention feature verified",
      "sourceUrl": "https://windsor.ai/connect/shopify-snowflake-integration/"
    },
    "subscription_analytics": {
      "value": "partial",
      "note": "Recharge connector exists; no prebuilt subscription analytics verified",
      "sourceUrl": "https://windsor.ai/connectors/"
    },
    "warehouse_access": {
      "value": "yes",
      "note": "Snowflake, BigQuery, Redshift, Databricks, Postgres and others on every plan",
      "sourceUrl": "https://windsor.ai/price-and-plans/"
    },
    "custom_metrics": {
      "value": "yes",
      "note": "Formula-based custom fields, including cross-source calculations",
      "sourceUrl": "https://windsor.ai/custom_fields/"
    },
    "custom_sql": {
      "value": "unknown",
      "note": "Custom fields use formulas; no in-product SQL found",
      "sourceUrl": "https://windsor.ai/custom_fields/"
    },
    "ai_assistant": {
      "value": "partial",
      "note": "Access via MCP server and connectors for Claude, ChatGPT, Copilot, Gemini; no in-app assistant verified",
      "sourceUrl": "https://windsor.ai/changelog/"
    },
    "mobile_app": {
      "value": "unknown",
      "note": "Not found",
      "sourceUrl": "https://windsor.ai/price-and-plans/"
    }
  },
  "recentChanges": [
    {
      "date": "2025-07",
      "change": "Launched Windsor.ai MCP server; volume-based pricing for database destinations introduced",
      "sourceUrl": "https://windsor.ai/changelog/"
    },
    {
      "date": "2025-08",
      "change": "15- and 30-minute refresh for database destinations on Professional plan",
      "sourceUrl": "https://windsor.ai/changelog/"
    },
    {
      "date": "2025-10",
      "change": "Microsoft Fabric destination launched",
      "sourceUrl": "https://windsor.ai/changelog/"
    },
    {
      "date": "2026-01",
      "change": "Claude connector went live; Cursor MCP connector followed",
      "sourceUrl": "https://windsor.ai/changelog/"
    },
    {
      "date": "2026-05",
      "change": "Claude Code plugin; MCP write actions for Facebook Ads and Google Ads campaigns",
      "sourceUrl": "https://windsor.ai/changelog/"
    },
    {
      "date": "2026-06",
      "change": "Per-connector MCP servers; Triple Whale connector added",
      "sourceUrl": "https://windsor.ai/changelog/"
    },
    {
      "date": "2026-10",
      "change": "eBay and Shopee Ads connectors added; Gemini Enterprise destination",
      "sourceUrl": "https://windsor.ai/changelog/"
    }
  ],
  "sources": [
    {
      "title": "Windsor.ai pricing and plans",
      "url": "https://windsor.ai/price-and-plans/"
    },
    {
      "title": "Windsor.ai pricing information (docs)",
      "url": "https://windsor.ai/documentation/pricing-information/"
    },
    {
      "title": "Windsor.ai connectors",
      "url": "https://windsor.ai/connectors/"
    },
    {
      "title": "Windsor.ai changelog",
      "url": "https://windsor.ai/changelog/"
    },
    {
      "title": "Shopify to Snowflake integration",
      "url": "https://windsor.ai/connect/shopify-snowflake-integration/"
    },
    {
      "title": "Amazon Seller Central connector",
      "url": "https://windsor.ai/connectors/amazon-seller-central/"
    },
    {
      "title": "Custom fields documentation",
      "url": "https://windsor.ai/custom_fields/"
    },
    {
      "title": "Data-driven attribution for affiliate ROI (blog)",
      "url": "https://www.windsor.ai/data-driven-attribution-for-affiliate-roi-connexity-awin-shopbots-et-al/"
    }
  ],
  "lastReviewed": "2026-11-03"
};
